import { useEffect, useRef, useState } from "react";

interface MenuItem {
  label: string;
  onSelect: () => void;
  /** When true, the item renders in destructive red. */
  destructive?: boolean;
  /** Optional test id for individual menu items. */
  "data-testid"?: string;
}

interface Props {
  items: MenuItem[];
  /** Test id for the trigger button. */
  triggerTestId?: string;
  /** Accessible label for the trigger button (defaults to "More actions"). */
  ariaLabel?: string;
}

/**
 * Plain "⋯" overflow menu. Dock-style (no library), click-outside-to-close.
 *
 * Used by ProjectDetailPage (top-right of header) and ProjectsListPage
 * (one per row). Keeps the surface clean — Stripe / Linear / hex.tech all
 * use this pattern for low-frequency destructive actions.
 */
export function OverflowMenu({ items, triggerTestId, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Click-outside-to-close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Escape closes too.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div ref={rootRef} style={styles.root}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel ?? "More actions"}
        onClick={() => setOpen((v) => !v)}
        data-testid={triggerTestId}
        style={styles.trigger}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <div role="menu" style={styles.menu}>
          {items.map((item) => (
            <button
              type="button"
              role="menuitem"
              key={item.label}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              data-testid={item["data-testid"]}
              style={item.destructive ? styles.itemDanger : styles.item}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: "relative",
    display: "inline-block",
  },
  trigger: {
    width: 32,
    height: 32,
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 6,
    fontSize: "1.125rem",
    lineHeight: 1,
    color: "#475569",
    cursor: "pointer",
    padding: 0,
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    minWidth: 180,
    background: "#ffffff",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 8,
    boxShadow: "0 8px 24px -6px rgba(15, 23, 42, 0.18)",
    padding: "4px",
    zIndex: 30,
  },
  item: {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    color: "#0a0e27",
    borderRadius: 4,
    cursor: "pointer",
  },
  itemDanger: {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    color: "#dc2626",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: 500,
  },
};
